/**
 * Durable token storage — bridges localStorage (web) and
 * @capacitor/preferences (iOS Keychain / Android EncryptedSharedPrefs) so
 * a driver's session survives:
 *   • WebView data purges triggered by iOS "Website Data" cleanup or by
 *     Android WebView memory pressure.
 *   • App upgrades that occasionally reset WKWebView storage on iOS.
 *   • Force-quit + relaunch of the Capacitor app.
 *
 * We ALWAYS mirror the value in `localStorage` because our axios request
 * interceptor is synchronous (see `src/lib/api.js`). Native Preferences
 * is used as the authoritative source-of-truth on cold-start: `hydrate()`
 * reads it and pushes into localStorage before the first API call.
 */
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

export const TOKEN_KEY = "saor_token";

const isNative = () =>
  typeof window !== "undefined" &&
  Capacitor &&
  typeof Capacitor.isNativePlatform === "function" &&
  Capacitor.isNativePlatform();

/**
 * Synchronously read the current token (used by axios interceptor).
 * `hydrate()` must have run at least once beforehand on native platforms.
 */
export function getTokenSync() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

/**
 * Persist the token in BOTH localStorage (sync mirror for axios) and
 * Capacitor Preferences (durable, survives WebView data purges).
 */
export async function setToken(token) {
  if (!token) return clearToken();
  try { localStorage.setItem(TOKEN_KEY, token); } catch (_) { /* ignore */ }
  if (isNative()) {
    try { await Preferences.set({ key: TOKEN_KEY, value: token }); } catch (_) { /* ignore */ }
  }
}

/**
 * Remove the token from every storage layer.
 */
export async function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
  if (isNative()) {
    try { await Preferences.remove({ key: TOKEN_KEY }); } catch (_) { /* ignore */ }
  }
}

/**
 * Cold-start hydration : on native platforms the WebView localStorage may
 * have been cleared by the OS. We restore it from Capacitor Preferences
 * (Keychain iOS / EncryptedSharedPrefs Android) BEFORE React mounts so the
 * axios interceptor picks up the token on the very first /auth/me call.
 *
 * Sur iOS Capacitor, le plugin Preferences peut ne pas être immédiatement
 * disponible au boot froid (le JS bridge n'est pas encore prêt). On
 * retry avec un backoff court pour garantir la restauration.
 *
 * Returns the hydrated token (or null).
 */
export async function hydrate() {
  if (!isNative()) return getTokenSync();
  // Try up to 5 times with 150ms delays (max ~750ms). The plugin bridge
  // is normally ready within 100-200ms of a cold start.
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      const { value } = await Preferences.get({ key: TOKEN_KEY });
      if (value) {
        try { localStorage.setItem(TOKEN_KEY, value); } catch (_) { /* ignore */ }
        return value;
      }
      // Preferences answered but had no value — either fresh install or
      // localStorage was the primary. Fall back to LS + mirror it.
      const ls = getTokenSync();
      if (ls) {
        try { await Preferences.set({ key: TOKEN_KEY, value: ls }); } catch (_) { /* ignore */ }
        return ls;
      }
      // Legitimately empty — no more retries needed.
      return null;
    } catch (_) {
      // Plugin bridge not ready yet. Wait and retry.
      await new Promise((r) => setTimeout(r, 150));
    }
  }
  // Last-ditch fallback: whatever LS has right now.
  return getTokenSync();
}
